import SEO from '../components/SEO';
import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Award, Cloud, Shield, Zap, Users, Factory, Warehouse, ShoppingCart, Sparkles, TrendingUp, Target, Globe, DollarSign, Clock, Calendar, Package, ArrowRight, Check, BarChart3, Workflow, Headphones, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const HomePageNewTest = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Ascarii - SAP Business One Partner | Premium Homepage Test"
        description="Test version of Ascarii homepage with premium design. SAP Premier Partner & AWS Advanced Partner delivering cloud ERP solutions."
        keywords="SAP Business One UK, cloud ERP, SAP partner"
      />

      {/* Hero Section - Premium Design */}
      <section className="relative pt-32 pb-24 lg:pt-40 lg:pb-32 overflow-hidden bg-gradient-to-b from-slate-50 to-white">
        {/* Ambient background */}
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-brand-100/30 rounded-full blur-[120px] -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute top-20 right-0 w-[400px] h-[400px] bg-mint-100/20 rounded-full blur-[100px] translate-x-1/2" />
        
        {/* Subtle grain */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' /%3E%3C/svg%3E")`,
          backgroundSize: '200px 200px'
        }} />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-mist rounded-full border border-line mb-8">
                <span className="text-xs font-medium text-ink/70">SAP Business One Premier Partner</span>
                <div className="w-1 h-1 rounded-full bg-gold-400" />
                <span className="text-xs font-medium text-ink/70">AWS Advanced Partner</span>
              </div>

              <h1 className="text-5xl lg:text-6xl xl:text-7xl font-bold text-ink mb-6 leading-[1.08] tracking-tight">
                Empowering Business Growth with<br />
                <span className="text-brand-500">SAP Business One</span>
              </h1>

              <p className="text-xl text-ink/60 mb-10 leading-relaxed max-w-xl">
                Transform from disconnected systems to complete business control. One powerful platform trusted by 80,000+ businesses worldwide.
              </p>

              <div className="flex flex-wrap gap-4 mb-10">
                <Link to="/contact">
                  <Button className="rounded-xl bg-brand-500 hover:bg-brand-600 text-white shadow-sm px-6 py-3 font-medium transition-all duration-200 focus:ring-4 focus:ring-brand-200">
                    Get in Touch
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/sap-business-one">
                  <Button variant="outline" className="rounded-xl border border-line bg-white hover:bg-brand-50 px-6 py-3 font-medium transition-all duration-200 focus:ring-4 focus:ring-brand-200">
                    Explore SAP Business One
                  </Button>
                </Link>
              </div>

              {/* Tag chips */}
              <div className="flex flex-wrap gap-2">
                {['Real-time Analytics', 'Cloud on AWS', 'Fast Implementation', 'Scalable Growth'].map((tag, i) => (
                  <span key={i} className="px-3 py-1 bg-brand-50 text-brand-700 rounded-full text-xs font-medium ring-1 ring-brand-100">
                    {tag}
                  </span>
                ))}
              </div>
            </motion.div>

            {/* Right: Featured Image Frame */}
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="relative"
            >
              {/* Decorative accents */}
              <div className="absolute -top-8 -right-8 w-48 h-48 bg-blue-100/30 rounded-full blur-2xl" />
              
              {/* Premium frame */}
              <div className="rounded-2xl bg-gradient-to-b from-white to-mist p-[1px]">
                <div className="rounded-2xl bg-white p-6">
                  <img 
                    src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=90" 
                    alt="SAP Business One Dashboard"
                    className="rounded-xl w-full h-auto shadow-[0_1px_0_rgba(15,23,42,0.06),0_16px_50px_rgba(15,23,42,0.06)]"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section - Clean Minimal */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: "100%", label: "Dedicated to SAP Business One", color: "text-brand-600" },
              { value: "99.995%", label: "Uptime Guarantee", color: "text-mint-600" },
              { value: "4.9/5", label: "Customer Satisfaction", color: "text-gold-600" },
              { value: "15+", label: "Years Experience", color: "text-brand-500" }
            ].map((stat, i) => (
              <motion.div 
                key={i} 
                initial={{ opacity: 0, y: 10 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.4, delay: i * 0.08 }} 
                viewport={{ once: true }}
                className="text-center group"
              >
                <div className={`text-4xl md:text-5xl font-bold ${stat.color} mb-2 transition-transform duration-200 group-hover:scale-105`}>
                  {stat.value}
                </div>
                <div className="text-sm text-ink/60 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SAP Business One Platform - Premium Split Layout */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-brand-50 text-brand-700 rounded-full text-xs font-medium ring-1 ring-brand-100 mb-6">
                <Target className="w-3 h-3" />
                The Platform
              </div>
              <h2 className="text-4xl font-bold text-ink mb-6 tracking-tight">SAP Business One - Complete Business Control</h2>
              <p className="text-lg text-ink/60 mb-8 leading-relaxed max-w-prose">
                One powerful platform trusted by 80,000+ businesses in 170 countries. Real-time visibility, automated workflows, and complete integration across finance, sales, inventory, and operations.
              </p>
              <div className="space-y-4 mb-8">
                {[
                  'Real-time analytics for faster decision-making',
                  'MTD compliant and HMRC approved',
                  'Cloud on AWS with 99.995% uptime',
                  'Fast implementation in weeks, not months',
                  'Scalable from 1 to 500+ users seamlessly'
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-brand-600" />
                    </div>
                    <span className="text-ink/70">{item}</span>
                  </div>
                ))}
              </div>
              <Link to="/sap-business-one">
                <Button className="rounded-xl bg-brand-500 hover:bg-brand-600 text-white shadow-sm px-6 py-3 font-medium transition-all duration-200 focus:ring-4 focus:ring-brand-200">
                  Explore SAP Business One
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="rounded-2xl border border-line bg-mist p-4">
                <img 
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=90" 
                  alt="SAP Business One Dashboard"
                  className="rounded-xl w-full h-auto"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Extensions - Premium Dark Section */}
      <section className="py-24 relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ 
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '32px 32px' 
          }} />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-sm font-medium mb-4 text-white">
                <Sparkles className="w-3.5 h-3.5" />
                Powerful Extensions
              </div>
              <h2 className="text-4xl font-bold text-white mb-4 tracking-tight">Transform SAP Business One into Your Perfect Solution</h2>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto">Enhance with specialist extensions for manufacturing, warehouse management, eCommerce, and productivity</p>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Factory, name: 'Beas Manufacturing', strapline: 'Complete shop floor control', desc: 'Advanced production planning, quality management, and real-time dashboards for manufacturing excellence.', stat: '900+ customers', link: '/products/beas-manufacturing' },
              { icon: Calendar, name: 'Netronic Manufacturing', strapline: 'Cloud production scheduling', desc: 'Visual planning boards, quality management, and paperless operations. Start within one day.', stat: 'Cloud-based', link: '/products/netronic-manufacturing' },
              { icon: Warehouse, name: 'Produmex WMS', strapline: 'Advanced warehouse management', desc: 'Optimize warehouse space with 99%+ picking accuracy and GS1 compliance.', stat: '99%+ accuracy', link: '/products/produmex-wms' },
              { icon: Package, name: 'Produmex Scan', strapline: 'Quick barcode scanning', desc: 'Barcode scanning deployed in 2 hours. Streamline picking with 100% accuracy.', stat: '2-hour setup', link: '/products/produmex-scan' },
              { icon: ShoppingCart, name: 'FocusPoint', strapline: 'AI-powered eCommerce', desc: 'SAP-certified eCommerce platform built exclusively for SAP Business One.', stat: 'SAP Certified', link: '/products/focuspoint' },
              { icon: Target, name: 'BPA Platform', strapline: 'Process automation', desc: 'Connect systems with 100+ pre-built connectors and drag-and-drop workflows.', stat: '100+ connectors', link: '/products/bpa-platform' }
            ].map((ext, i) => (
              <Link key={i} to={ext.link}>
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="group h-full bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300 hover:-translate-y-0.5"
                >
                  <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-4 group-hover:bg-white/20 transition-colors">
                    <ext.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{ext.name}</h3>
                  <p className="text-sm text-cyan-300 mb-3 font-medium">{ext.strapline}</p>
                  <p className="text-blue-100/80 text-sm leading-relaxed mb-4">{ext.desc}</p>
                  <div className="inline-flex px-3 py-1 bg-white/10 rounded-full text-xs text-cyan-300 font-medium">
                    {ext.stat}
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Credentials - Premium Cards */}
      <section className="py-20 bg-gradient-to-b from-mist/50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-mist rounded-full border border-line mb-4">
                <Award className="w-3.5 h-3.5 text-brand-500" />
                <span className="text-xs font-medium text-ink/70">Trusted Partners</span>
              </div>
              <h2 className="text-4xl font-bold text-ink mb-4 tracking-tight">World-Class Technology Partners</h2>
              <p className="text-lg text-ink/60 max-w-2xl mx-auto">Industry leaders delivering the best technology stack for your business</p>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { 
                name: 'SAP Business One', 
                level: 'Premier Partner',
                desc: '15+ years expertise, multiple awards, premier UK partner',
                icon: Award,
                highlight: 'gold'
              },
              { 
                name: 'AWS', 
                level: 'Advanced Partner',
                desc: '99.995% uptime, enterprise-grade cloud infrastructure',
                icon: Cloud,
                highlight: 'brand'
              },
              { 
                name: 'Boyum Solutions', 
                level: 'Gold Partner',
                desc: 'Manufacturing and productivity solutions for 900+ customers',
                icon: Sparkles,
                highlight: 'mint'
              }
            ].map((partner, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="group"
              >
                <div className={`rounded-2xl ${partner.highlight === 'gold' ? 'bg-gradient-to-br from-gold-50 to-white p-[1px]' : partner.highlight === 'mint' ? 'bg-gradient-to-br from-mint-50 to-white p-[1px]' : 'bg-gradient-to-br from-brand-50 to-white p-[1px]'} hover:-translate-y-0.5 transition-all duration-300 shadow-[0_1px_0_rgba(15,23,42,0.06),0_16px_50px_rgba(15,23,42,0.06)] hover:shadow-[0_1px_0_rgba(15,23,42,0.08),0_24px_60px_rgba(15,23,42,0.08)]`}>
                  <div className="rounded-2xl bg-white p-8 h-full">
                    <div className={`w-12 h-12 rounded-xl ${partner.highlight === 'gold' ? 'bg-gold-50' : partner.highlight === 'mint' ? 'bg-mint-50' : 'bg-brand-50'} flex items-center justify-center mb-4`}>
                      <partner.icon className={`w-6 h-6 ${partner.highlight === 'gold' ? 'text-gold-600' : partner.highlight === 'mint' ? 'text-mint-600' : 'text-brand-600'}`} />
                    </div>
                    <div className="text-sm font-medium text-ink/50 mb-1">{partner.name}</div>
                    <div className="text-lg font-semibold text-ink mb-3">{partner.level}</div>
                    <p className="text-sm text-ink/60 leading-relaxed">{partner.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories - Clean Cards */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-mist rounded-full border border-line mb-4">
                <CheckCircle2 className="w-3.5 h-3.5 text-brand-500" />
                <span className="text-xs font-medium text-ink/70">Customer Success</span>
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-ink mb-4 tracking-tight">Businesses Thriving with Ascarii</h2>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { 
                company: 'Hawthorn Trading', 
                result: 'Saved £13,000 in first quarter through better purchasing power and data visibility', 
                sector: 'Wholesale',
                featured: false
              },
              { 
                company: 'Abbeydale Direct', 
                result: 'Taken our business from small thinking to making all things possible with SAP foundation', 
                sector: 'Distribution',
                featured: true
              },
              { 
                company: 'Hill & Smith Group', 
                result: 'Standardised ERP across multiple subsidiaries with faster M&A integration', 
                sector: 'Manufacturing Group',
                featured: false
              }
            ].map((story, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="group"
              >
                <div className={`h-full rounded-2xl ${story.featured ? 'bg-gradient-to-br from-gold-50 to-white p-[1px]' : 'border border-line bg-white'} hover:-translate-y-0.5 transition-all duration-300 shadow-[0_1px_0_rgba(15,23,42,0.06),0_16px_50px_rgba(15,23,42,0.06)] hover:shadow-[0_1px_0_rgba(15,23,42,0.08),0_24px_60px_rgba(15,23,42,0.08)]`}>
                  <div className={`${story.featured ? 'rounded-2xl bg-white' : ''} p-8`}>
                    <div className="inline-flex px-3 py-1 bg-brand-50 text-brand-700 rounded-full text-xs font-medium ring-1 ring-brand-100 mb-4">
                      {story.sector}
                    </div>
                    <h3 className="text-xl font-semibold text-ink mb-4">{story.company}</h3>
                    <p className="text-sm text-ink/70 italic leading-relaxed">"{story.result}"</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Promise - Clean with Integrated Image */}
      <section className="py-24 bg-gradient-to-b from-slate-50/50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="rounded-2xl border border-line bg-mist p-4">
                <img 
                  src="https://images.unsplash.com/photo-1517048676732-d65bc937f952?w=800&q=85" 
                  alt="Partnership and collaboration"
                  className="rounded-xl w-full h-auto"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-mint-50 text-mint-700 rounded-full text-xs font-medium ring-1 ring-mint-100 mb-6">
                <Headphones className="w-3 h-3" />
                Your Partner
              </div>
              <h2 className="text-4xl font-bold text-ink mb-6 tracking-tight">Your Success is Our Mission</h2>
              <p className="text-lg text-ink/60 mb-8 leading-relaxed max-w-prose">
                We're not just a software vendor - we're your transformation partner. We'll never leave you. Together, we'll take your business to new heights.
              </p>
              <div className="grid grid-cols-3 gap-6">
                {[
                  { value: '100%', label: 'SAP B1 Focus', color: 'brand' },
                  { value: '4.9/5', label: 'Satisfaction', color: 'gold' },
                  { value: '15+', label: 'Years', color: 'brand' }
                ].map((stat, i) => (
                  <div key={i} className="text-center">
                    <div className={`text-3xl font-bold ${stat.color === 'gold' ? 'text-gold-600' : 'text-brand-600'} mb-1`}>{stat.value}</div>
                    <div className="text-xs text-ink/60">{stat.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA - Premium Blue */}
      <section className="py-24 bg-gradient-to-br from-brand-600 to-brand-700 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ 
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '32px 32px' 
          }} />
        </div>

        <div className="max-w-4xl mx-auto px-6 lg:px-8 relative text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight">
              Ready to Transform Your Business?
            </h2>
            <p className="text-xl text-blue-100 mb-12 leading-relaxed">
              Join 80,000+ businesses worldwide running on SAP Business One with Ascarii partnership
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button className="rounded-xl bg-white text-brand-600 hover:bg-blue-50 px-8 py-3 font-medium transition-all duration-200 shadow-sm hover:shadow-md">
                  Get in Touch
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/sap-business-one">
                <Button className="rounded-xl bg-brand-500 hover:bg-brand-600 text-white px-8 py-3 font-medium border border-brand-400/30 transition-all duration-200">
                  Explore SAP Business One
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePageNewTest;
